import { filasSinCuadrar } from './sinCuadrar';
import type { SinCuadrar } from './sinCuadrar';

/**
 * Filas de «Por hacer» en Inicio. La insignia cuenta filas (lo que se ve),
 * no categorías: con tres categorías en rojo hay una sola fila «Cubrir».
 */
export type FilaPorHacer =
  | { tipo: 'descuadre'; importe: number }
  | { tipo: 'revisar'; n: number }
  | { tipo: 'vencido'; id: string }
  | { tipo: 'cambio-cuenta'; id: string }
  | { tipo: 'en-rojo'; n: number }
  | { tipo: 'ingresos-sin-categoria'; importe: number }
  | { tipo: 'asignar' }
  | { tipo: 'sobreasignado' }
  | { tipo: 'sin-cuadrar'; cuenta: SinCuadrar }
  | { tipo: 'sin-cuadrar-grupo'; n: number };

export type DatosPorHacer = {
  /** Movimientos por revisar, tengan o no categoría (una sola fila). */
  nuevos: number;
  vencidos: readonly string[];
  /** Pagos fijos cuyo último cargo salió de otra cuenta (ids de categoría). */
  cambiosCuenta?: readonly string[];
  enRojo: number;
  /**
   * Suma (en céntimos) de los ingresos del mes sin categoría: no entran en
   * «Listo para asignar» hasta que se les pone una categoría de ingreso.
   */
  ingresosSinCategoria: number;
  toBudget: number;
  /** Cuentas con más de un mes sin cuadrar con el banco (ver `sinCuadrar`). */
  sinCuadrar?: readonly SinCuadrar[];
  /**
   * Descuadre del presupuesto en céntimos, ya descontado lo sin categoría
   * (ver `cuadre.ts`): 0 o ausente = cuadra.
   */
  descuadre?: number;
};

/** En el orden en que se pintan. Un pago vencido = una fila. */
export function filasPorHacer(d: DatosPorHacer): FilaPorHacer[] {
  const filas: FilaPorHacer[] = [];
  // Primero: es un fallo de datos, no una tarea del mes.
  if (d.descuadre) {
    filas.push({ tipo: 'descuadre', importe: d.descuadre });
  }
  if (d.nuevos > 0) {
    filas.push({ tipo: 'revisar', n: d.nuevos });
  }
  for (const id of d.vencidos) {
    filas.push({ tipo: 'vencido', id });
  }
  for (const id of d.cambiosCuenta ?? []) {
    filas.push({ tipo: 'cambio-cuenta', id });
  }
  if (d.enRojo > 0) {
    filas.push({ tipo: 'en-rojo', n: d.enRojo });
  }
  // Justo antes de «Asignar»: explica por qué el sueldo aún no aparece ahí.
  if (d.ingresosSinCategoria > 0) {
    filas.push({
      tipo: 'ingresos-sin-categoria',
      importe: d.ingresosSinCategoria,
    });
  }
  if (d.toBudget > 0) {
    filas.push({ tipo: 'asignar' });
  } else if (d.toBudget < 0) {
    filas.push({ tipo: 'sobreasignado' });
  }
  // Al final: es mantenimiento, no corre prisa como el dinero del mes.
  for (const f of filasSinCuadrar(d.sinCuadrar ?? [])) {
    filas.push(
      f.tipo === 'grupo'
        ? { tipo: 'sin-cuadrar-grupo', n: f.n }
        : { tipo: 'sin-cuadrar', cuenta: f.cuenta },
    );
  }
  return filas;
}
